import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin, Clock, Send, Check } from "lucide-react";
import TopBar from "@/components/site/TopBar";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import CartDrawer from "@/components/site/CartDrawer";
import { BUSINESS } from "@/mock";

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Store in localStorage as demo
    const existing = JSON.parse(localStorage.getItem("jhumar_enquiries") || "[]");
    existing.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem("jhumar_enquiries", JSON.stringify(existing));
    setSent(true);
    setForm({ name: "", email: "", phone: "", message: "" });
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <div className="min-h-screen bg-[#0b0b0c] text-[#f4ead5]">
      <TopBar />
      <Header />

      <section className="relative overflow-hidden bg-[#0b0b0c] border-b border-[#1c1c1e]">
        <div
          className="absolute inset-0 opacity-[0.25]"
          style={{
            backgroundImage:
              "radial-gradient(900px 400px at 80% 20%, rgba(201,162,74,0.3), transparent 60%)",
          }}
        />
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 pt-14 pb-16">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#a9a294] hover:text-[#c9a24a] text-xs uppercase tracking-[0.25em] mb-8 transition-colors"
          >
            <ArrowLeft size={14} />
            Back to home
          </Link>
          <div className="text-[#c9a24a] uppercase text-[11px] tracking-[0.4em] mb-4">
            Get in touch
          </div>
          <h1 className="font-serif text-[#f4ead5] text-4xl md:text-6xl leading-tight">
            Contact <span className="italic">us</span>
          </h1>
          <p className="mt-6 max-w-xl text-[#c4bba6] text-base leading-relaxed">
            Visit our Wanawadi showroom, drop us an email, or fill in the form
            below. Our team replies within one working day.
          </p>
        </div>
      </section>

      <section className="bg-[#0b0b0c] py-16">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12">
          <div className="space-y-8">
            <div className="border border-[#1c1c1e] bg-[#111112] p-8">
              <div className="font-serif text-[#f4ead5] text-2xl mb-6">
                Reach us directly
              </div>
              <div className="space-y-5 text-[#e9d9b0] text-[15px]">
                <div className="flex gap-4">
                  <MapPin size={18} className="text-[#c9a24a] mt-1 flex-shrink-0" />
                  <div className="leading-relaxed">
                    <div className="text-[#a9a294] text-xs uppercase tracking-[0.2em] mb-1">Showroom</div>
                    <div>{BUSINESS.address.line1}</div>
                    <div>{BUSINESS.address.line2}</div>
                    <div>{BUSINESS.address.line3}</div>
                    <div>{BUSINESS.address.city} – {BUSINESS.address.pin}, {BUSINESS.address.state}</div>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Phone size={18} className="text-[#c9a24a] mt-1" />
                  <div>
                    <div className="text-[#a9a294] text-xs uppercase tracking-[0.2em] mb-1">Phone</div>
                    <a href={`tel:${BUSINESS.phone}`} className="hover:text-[#c9a24a] transition-colors">
                      {BUSINESS.phone}
                    </a>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Mail size={18} className="text-[#c9a24a] mt-1" />
                  <div>
                    <div className="text-[#a9a294] text-xs uppercase tracking-[0.2em] mb-1">Email</div>
                    <a href={`mailto:${BUSINESS.email}`} className="hover:text-[#c9a24a] transition-colors">
                      {BUSINESS.email}
                    </a>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Clock size={18} className="text-[#c9a24a] mt-1" />
                  <div>
                    <div className="text-[#a9a294] text-xs uppercase tracking-[0.2em] mb-1">Hours</div>
                    <div>{BUSINESS.hours}</div>
                    <div className="text-[#a9a294] text-sm mt-1">Sunday: Closed</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-[#c9a24a]/40 bg-[#111112] p-8">
              <div className="text-[#c9a24a] uppercase text-[11px] tracking-[0.3em] mb-3">
                GST details
              </div>
              <div className="text-[#f4ead5] font-serif text-lg">{BUSINESS.legalName}</div>
              <div className="text-[#a9a294] text-sm mt-2">GSTIN: {BUSINESS.gstin}</div>
            </div>
          </div>

          <div className="border border-[#1c1c1e] bg-[#111112] p-8">
            <div className="font-serif text-[#f4ead5] text-2xl mb-2">
              Send us a message
            </div>
            <p className="text-[#a9a294] text-sm mb-6">
              Have a question about a product, installation, or order? We're
              happy to help.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#a9a294] mb-2">
                  Name
                </label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-[#0b0b0c] border border-[#2a2724] focus:border-[#c9a24a] outline-none px-4 py-3 text-[#f4ead5] text-sm transition-colors"
                  placeholder="Your full name"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-[#a9a294] mb-2">
                    Email
                  </label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-[#0b0b0c] border border-[#2a2724] focus:border-[#c9a24a] outline-none px-4 py-3 text-[#f4ead5] text-sm transition-colors"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-[#a9a294] mb-2">
                    Phone
                  </label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full bg-[#0b0b0c] border border-[#2a2724] focus:border-[#c9a24a] outline-none px-4 py-3 text-[#f4ead5] text-sm transition-colors"
                    placeholder="+91 …"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#a9a294] mb-2">
                  Message
                </label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-[#0b0b0c] border border-[#2a2724] focus:border-[#c9a24a] outline-none px-4 py-3 text-[#f4ead5] text-sm transition-colors resize-none"
                  placeholder="Tell us about your space, ceiling height, or the product you're interested in…"
                />
              </div>
              <button
                type="submit"
                disabled={sent}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#c9a24a] hover:bg-[#b48d38] disabled:opacity-70 text-[#0b0b0c] py-4 text-[12px] uppercase tracking-[0.25em] font-medium transition-colors"
              >
                {sent ? (
                  <>
                    <Check size={16} /> Message sent — we'll be in touch
                  </>
                ) : (
                  <>
                    <Send size={14} /> Send message
                  </>
                )}
              </button>
              <div className="text-[10px] text-[#a9a294] text-center">
                By sending, you agree to our{" "}
                <Link to="/privacy" className="text-[#c9a24a] hover:underline">Privacy Policy</Link>.
              </div>
            </form>
          </div>
        </div>
      </section>

      <Footer />
      <CartDrawer />
    </div>
  );
};

export default ContactPage;
